import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTypesForBrand, fetchBrandById, fetchBrands } from "../api/BrandAPIs";
import { createType, updateType, deleteType, addTypeStock, updateTypeStock, fetchAssignedCaseStyleIds, setTypeCaseStyles, fetchTypes, setTypeChildTypes } from "../api/TypeAPIs";
import { fetchCaseStyles } from "../api/CatalogAPIs";
import { displayAr } from "./Catalog/catalogUi";
import { printTypeNameLabels } from "../utils/qzTray";
import {
  exportBrandAvailableTypesCsv,
  exportAllBrandsAvailableTypesCsv,
  groupAvailableTypesByBrand,
  downloadBrandAvailableTypesPdf,
  downloadAllBrandsAvailableTypesPdf,
  getAvailableTypes,
  sortTypesBySalesDesc,
  getSalesRange,
  getSalesHeatStyle,
} from "../utils/availableTypesReport";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Layers,
  AlertCircle,
  Tag,
  TrendingUp,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  ChevronUp,
  ChevronDown,
  Printer,
  PackagePlus,
  Package,
  FileDown,
  FileSpreadsheet,
} from "lucide-react";

const mapApiType = (type, brandId, allTypes = []) => {
  const typeId = type.typeId;
  const ownSales = type.sales || 0;
  const children = (allTypes || []).filter((t) => t.mainTypeId === typeId);
  const servedTypes = [
    { id: typeId, name: type.name, sales: ownSales, isParent: true },
    ...children.map((t) => ({
      id: t.typeId,
      name: t.name,
      sales: t.sales || 0,
      isParent: false,
    })),
  ];
  const totalSales = servedTypes.reduce((sum, row) => sum + (row.sales || 0), 0);
  const main = type.mainTypeId
    ? (allTypes || []).find((t) => t.typeId === type.mainTypeId)
    : null;
  return {
    id: typeId,
    name: type.name,
    sales: ownSales,
    ownSales,
    totalSales,
    isAvailable: type.isAvailable !== undefined ? type.isAvailable : true,
    brandId: parseInt(brandId, 10),
    stockQuantity: type.stockQuantity ?? 0,
    lowStockThreshold: type.lowStockThreshold ?? 0,
    unavailableThreshold: type.unavailableThreshold ?? 0,
    isLowStock: !!type.isLowStock,
    lastStockUpdate: type.lastStockUpdate || null,
    mainTypeId: type.mainTypeId || null,
    mainTypeName: main?.name || null,
    isMain: !type.mainTypeId,
    servedTypes,
    servesCount: servedTypes.length,
    childTypeIds: children.map((t) => t.typeId),
  };
};

const applyStockResponse = (type, stock) => ({
  ...type,
  stockQuantity: stock.stockQuantity ?? type.stockQuantity,
  lowStockThreshold: stock.lowStockThreshold ?? type.lowStockThreshold,
  unavailableThreshold: stock.unavailableThreshold ?? type.unavailableThreshold,
  isLowStock: stock.isLowStock ?? type.isLowStock,
  isAvailable: stock.isAvailable ?? type.isAvailable,
  lastStockUpdate: stock.lastStockUpdate || type.lastStockUpdate,
  sales: stock.sales ?? type.sales,
  ownSales: stock.sales ?? type.ownSales ?? type.sales,
  totalSales:
    (stock.sales ?? type.ownSales ?? type.sales ?? 0) +
    Math.max((type.totalSales || 0) - (type.ownSales ?? type.sales ?? 0), 0),
});

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <>
        <div className="animate-pulse hidden md:block">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex gap-4 border-b py-3">
              <div className="w-12 h-12 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="animate-pulse flex flex-col gap-4 md:hidden">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
              <div className="h-6 bg-gray-200 rounded w-2/3"></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="h-16 bg-gray-200 rounded-xl"></div>
                <div className="h-16 bg-gray-200 rounded-xl"></div>
              </div>
              <div className="h-10 bg-gray-200 rounded-xl"></div>
            </div>
          ))}
        </div>
      </>
    );
  }
  return null;
};

const getTypeStockTone = (type) => {
  if ((type.stockQuantity || 0) <= 0) {
    return {
      card: "border-red-200",
      banner: "bg-red-600 text-white",
      bannerText: "خلص من المخزن",
      stockWrap: "bg-red-50 border-red-100",
      stockValue: "text-red-600",
    };
  }
  if (type.isLowStock) {
    return {
      card: "border-amber-200",
      banner: "bg-amber-400 text-amber-950",
      bannerText: "محتاج شراء",
      stockWrap: "bg-amber-50 border-amber-100",
      stockValue: "text-amber-700",
    };
  }
  return {
    card: type.isAvailable ? "border-purple-100" : "border-gray-200",
    banner: null,
    bannerText: "",
    stockWrap: "bg-blue-50 border-blue-100",
    stockValue: "text-blue-700",
  };
};

const TypeMobileCard = ({
  type,
  isLoading,
  printingId,
  isPrintingAll,
  onToggleAvailability,
  onAddStock,
  onPrint,
  onEdit,
  onDelete,
  onOpenSales,
}) => {
  const tone = getTypeStockTone(type);
  const isPrinting = printingId === type.id;

  return (
    <article className={`rounded-2xl border bg-white shadow-sm overflow-hidden ${tone.card}`}>
      {tone.banner && (
        <div className={`px-4 py-1.5 text-center text-sm font-bold ${tone.banner}`}>
          {tone.bannerText}
        </div>
      )}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-[1.15rem] font-bold text-gray-900 leading-snug break-words">
            <button
              type="button"
              onClick={() => onOpenSales?.(type)}
              className="text-right hover:text-purple-700 underline decoration-purple-200 underline-offset-4"
            >
              {type.name}
            </button>
          </h3>
          <span className="shrink-0 font-mono text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-lg">
            #{type.id}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
            نوع رئيسي
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
            يخدم {type.servesCount || 1} أنواع
          </span>
        </div>
        {(type.servedTypes || []).length > 1 && (
          <p className="text-xs text-gray-600 leading-relaxed">
            {(type.servedTypes || []).map((s) => s.name).join(" · ")}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onOpenSales?.(type)}
            className="rounded-xl border border-green-100 bg-green-50 px-3 py-2.5 text-center"
          >
            <p className="text-xs font-medium text-green-700">المبيعات</p>
            <p className="text-2xl font-black text-green-700 tabular-nums leading-tight">
              {type.totalSales ?? type.sales ?? 0}
            </p>
            {(type.servesCount || 1) > 1 && (
              <p className="text-[10px] text-green-700/80 mt-0.5">مجموع الشكل</p>
            )}
          </button>
          <div className={`rounded-xl border px-3 py-2.5 text-center ${tone.stockWrap}`}>
            <p className="text-xs font-medium text-gray-600">المخزن</p>
            <p className={`text-2xl font-black tabular-nums leading-tight ${tone.stockValue}`}>
              {type.stockQuantity ?? 0}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            حد الإشعار <b className="text-gray-800">{type.lowStockThreshold ?? 0}</b>
          </span>
          <span>
            حد التوقف <b className="text-gray-800">{type.unavailableThreshold ?? 0}</b>
          </span>
        </div>

        <button
          type="button"
          onClick={() => onToggleAvailability(type)}
          disabled={isLoading}
          className={`w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${
            type.isAvailable
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          {type.isAvailable ? <CheckCircle size={16} /> : <XCircle size={16} />}
          {type.isAvailable ? "متاح" : "غير متاح"}
        </button>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onAddStock(type)}
            disabled={isLoading}
            className="bg-blue-600 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <PackagePlus size={16} />
            ستوك
          </button>
          <button
            type="button"
            onClick={() => onPrint(type)}
            disabled={isLoading || isPrinting || isPrintingAll}
            className="bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <Printer size={16} className={isPrinting ? "animate-pulse" : ""} />
            {isPrinting ? "..." : "طباعة"}
          </button>
          <button
            type="button"
            onClick={() => onEdit(type)}
            disabled={isLoading}
            className="bg-amber-500 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <Edit size={16} />
            تعديل
          </button>
          <button
            type="button"
            onClick={() => onDelete(type.id)}
            disabled={isLoading}
            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          >
            <Trash2 size={16} />
            حذف
          </button>
        </div>
      </div>
    </article>
  );
};

const TypesPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [brandData, setBrandData] = useState(null);
  const [newType, setNewType] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [printingId, setPrintingId] = useState(null);
  const [isPrintingAll, setIsPrintingAll] = useState(false);
  const [addingStockFor, setAddingStockFor] = useState(null);
  const [addStockQty, setAddStockQty] = useState("");
  const [caseStyles, setCaseStyles] = useState([]);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isHeatmapOpen, setIsHeatmapOpen] = useState(false);
  const [catalogTypes, setCatalogTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [childPickerScope, setChildPickerScope] = useState("brand");
  const [childPickerSearch, setChildPickerSearch] = useState("");
  const [salesDetailType, setSalesDetailType] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [brandResponse, typesResponse, stylesResponse, allTypesResponse, brandsResponse] =
          await Promise.all([
            fetchBrandById(brandId),
            fetchTypesForBrand(brandId),
            fetchCaseStyles().catch(() => []),
            fetchTypes().catch(() => []),
            fetchBrands().catch(() => []),
          ]);

        setBrandData(brandResponse);
        setCaseStyles(Array.isArray(stylesResponse) ? stylesResponse : []);
        setBrands(Array.isArray(brandsResponse) ? brandsResponse : []);

        const catalog = Array.isArray(allTypesResponse) ? allTypesResponse : [];
        setCatalogTypes(
          catalog.map((type) => mapApiType(type, type.brandId, catalog))
        );

        if (typesResponse && typesResponse.length > 0) {
          setTypes(
            typesResponse
              .map((type) => mapApiType(type, brandId, catalog.length ? catalog : typesResponse))
              .sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
          );
        } else {
          setTypes([]);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message || "فشل في تحميل البيانات");
      } finally {
        setIsLoading(false);
      }
    };

    if (brandId) {
      loadData();
    }
  }, [brandId]);

  const handleInputChange = (e, setState, state) => {
    const { name, value, type: inputType, checked } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: inputType === "checkbox" ? checked : value,
    }));
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      const createPayload = {
        ...newType,
        brandId: parseInt(brandId)
      };
      const created = await createType(createPayload);
      
      const newTypeItem = mapApiType(created, brandId);
      
      setTypes(prevTypes => 
        [...prevTypes, newTypeItem].sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
      );
      setNewType(null);
    } catch (err) {
      console.error("Error creating type:", err);
      alert("فشل في إنشاء النوع: " + err.message);
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {
        ...editingType,
        brandId: parseInt(brandId)
      };
      await updateType(editingType.id, updatePayload);
      await setTypeCaseStyles(editingType.id, editingType.caseStyleIds || []);
      const selectedChildren = editingType.childTypeIds || [];
      const catalog = catalogTypes.length ? catalogTypes : types;
      const moving = selectedChildren.filter((childId) => {
        const current = catalog.find((t) => t.id === childId);
        return current?.mainTypeId && current.mainTypeId !== editingType.id;
      });
      if (moving.length > 0) {
        const names = moving
          .map((id) => catalog.find((t) => t.id === id)?.name || id)
          .join("، ");
        const ok = window.confirm(
          `الأنواع التالية تابعة لنوع رئيسي آخر وسيتم تغيير مالك المخزون:\n${names}\nهل تريد المتابعة؟`
        );
        if (!ok) return;
      }
      await setTypeChildTypes(editingType.id, selectedChildren);
      let nextType = {
        ...editingType,
        name: editingType.name,
        isAvailable: editingType.isAvailable,
        brandId: parseInt(brandId, 10)
      };
      if (
        editingType.stockQuantity !== undefined ||
        editingType.lowStockThreshold !== undefined ||
        editingType.unavailableThreshold !== undefined
      ) {
        const stock = await updateTypeStock(
          editingType.id,
          Number(editingType.stockQuantity),
          Number(editingType.lowStockThreshold),
          Number(editingType.unavailableThreshold)
        );
        nextType = applyStockResponse(nextType, stock);
      }

      setTypes(prevTypes =>
        prevTypes.map(type =>
          type.id === editingType.id ? { ...type, ...nextType } : type
        ).sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
      );
      setEditingType(null);
      await refreshData();
    } catch (err) {
      console.error("Error updating type:", err);
      alert("فشل في تحديث النوع: " + err.message);
    }
  };

  const handleToggleAvailability = async (type) => {
    try {
      const updatedTypePayload = { 
        ...type, 
        isAvailable: !type.isAvailable,
        brandId: parseInt(brandId)
      };
      await updateType(type.id, updatedTypePayload);
      
      setTypes(prevTypes =>
        prevTypes.map(t => 
          t.id === type.id
            ? { ...t, isAvailable: !t.isAvailable }
            : t
        )
      );
    } catch (err) {
      console.error("Error updating type availability:", err);
      alert("فشل في تحديث حالة التوافر: " + err.message);
    }
  };

  const handleDeleteType = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا النوع؟")) {
      try {
        await deleteType(id);
        setTypes(prevTypes => prevTypes.filter((type) => type.id !== id));
      } catch (err) {
        console.error("Error deleting type:", err);
        alert("فشل في حذف النوع: " + err.message);
      }
    }
  };

  const openEditType = async (type) => {
    let assigned = [];
    try {
      assigned = await fetchAssignedCaseStyleIds(type.id);
    } catch (_) {
      assigned = [];
    }
    setChildPickerScope("brand");
    setChildPickerSearch("");
    setEditingType({
      id: type.id,
      name: type.name,
      sales: type.sales,
      isAvailable: type.isAvailable,
      brandId: type.brandId,
      stockQuantity: type.stockQuantity ?? 0,
      lowStockThreshold: type.lowStockThreshold ?? 0,
      unavailableThreshold: type.unavailableThreshold ?? 0,
      caseStyleIds: Array.isArray(assigned) ? assigned : [],
      childTypeIds: type.childTypeIds || [],
    });
  };

  const openAddStock = (type) => {
    setAddingStockFor(type);
    setAddStockQty("");
  };

  const refreshData = async () => {
    try {
      setIsLoading(true);
      const [typesResponse, allTypesResponse] = await Promise.all([
        fetchTypesForBrand(brandId),
        fetchTypes().catch(() => []),
      ]);
      const catalog = Array.isArray(allTypesResponse) ? allTypesResponse : [];
      setCatalogTypes(catalog.map((type) => mapApiType(type, type.brandId, catalog)));

      if (typesResponse && typesResponse.length > 0) {
        setTypes(
          typesResponse
            .map((type) => mapApiType(type, brandId, catalog.length ? catalog : typesResponse))
            .sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
        );
      } else {
        setTypes([]);
      }
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError(err.message || "فشل في تحديث البيانات");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    if (!addingStockFor) return;
    const qty = Number(addStockQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("أدخل عدد قطع أكبر من صفر");
      return;
    }
    try {
      const stock = await addTypeStock(addingStockFor.id, qty);
      setTypes((prev) =>
        prev.map((type) =>
          type.id === addingStockFor.id ? applyStockResponse(type, stock) : type
        )
      );
      toast.success(`تمت إضافة ${qty} لـ ${addingStockFor.name}`);
      setAddingStockFor(null);
      setAddStockQty("");
    } catch (err) {
      console.error("Error adding stock:", err);
      toast.error(err.message || "فشل إضافة المخزون");
    }
  };

  // Filter types based on search term
  const mainTypes = types.filter((type) => type.isMain);
  const childSearchHits = types.filter((type) => {
    if (type.isMain || !searchTerm) return false;
    const term = searchTerm.toLowerCase();
    return type.name.toLowerCase().includes(term) || type.id.toString().includes(term);
  });
  const filteredTypes = mainTypes.filter((type) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        type.name.toLowerCase().includes(term) ||
        type.id.toString().includes(term) ||
        (type.servedTypes || []).some(
          (served) =>
            String(served.name || "").toLowerCase().includes(term) ||
            String(served.id).includes(term)
        );
      if (!matchesSearch) return false;
    }
    if (stockFilter === "restock") return type.isLowStock;
    if (stockFilter === "out") return (type.stockQuantity || 0) <= 0;
    if (stockFilter === "belowStop") {
      return type.unavailableThreshold > 0 && type.stockQuantity <= type.unavailableThreshold;
    }
    return true;
  });

  const restockCount = mainTypes.filter((t) => t.isLowStock).length;
  const outCount = mainTypes.filter((t) => (t.stockQuantity || 0) <= 0).length;
  const unitsOnHand = mainTypes.reduce((sum, t) => sum + (t.stockQuantity || 0), 0);
  const availableTypes = sortTypesBySalesDesc(getAvailableTypes(types));
  const { min: minAvailableSales, max: maxAvailableSales } = getSalesRange(availableTypes);
  const availableSalesTotal = availableTypes.reduce((sum, type) => sum + (type.sales || 0), 0);

  const handleDownloadBrandReport = () => {
    try {
      const count = exportBrandAvailableTypesCsv(brandData?.name, types);
      toast.success(`تم تحميل تقرير ${count} نوع متاح`);
    } catch (err) {
      toast.warning(err.message || "فشل تحميل التقرير");
    }
  };

  const handleDownloadBrandPdf = async () => {
    try {
      setIsDownloadingPdf(true);
      await downloadBrandAvailableTypesPdf(brandData?.name, types);
      toast.success("تم تحميل ملف PDF على جهازك");
    } catch (err) {
      toast.warning(err.message || "فشل تحميل PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const loadAllBrandsAvailableGroups = async () => {
    const [brands, allTypes] = await Promise.all([fetchBrands(), fetchTypes()]);
    return groupAvailableTypesByBrand(brands, allTypes);
  };

  const handleDownloadAllBrandsReport = async () => {
    try {
      setIsExportingAll(true);
      const grouped = await loadAllBrandsAvailableGroups();
      const count = exportAllBrandsAvailableTypesCsv(grouped);
      toast.success(`تم تحميل تقرير ${count} نوع متاح لكل البراندات`);
    } catch (err) {
      toast.error(err.message || "فشل تحميل تقرير كل البراندات");
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleDownloadAllBrandsPdf = async () => {
    try {
      setIsDownloadingPdf(true);
      const grouped = await loadAllBrandsAvailableGroups();
      await downloadAllBrandsAvailableTypesPdf(grouped);
      toast.success("تم تحميل ملف PDF لكل البراندات");
    } catch (err) {
      toast.error(err.message || "فشل تحميل PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handlePrintType = async (type) => {
    setPrintingId(type.id);
    try {
      await printTypeNameLabels(type.name);
      toast.success(`تم طباعة: ${type.name}`);
    } catch (err) {
      console.error("Print type failed:", err);
      toast.error(err.message || "فشل الطباعة");
    } finally {
      setPrintingId(null);
    }
  };

  const handlePrintAllTypes = async () => {
    const toPrint = types
      .filter((t) => (t.sales || 0) > 10 && t.name)
      .slice()
      .sort((a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true })
      );
    const names = toPrint.map((t) => t.name);
    if (names.length === 0) {
      toast.warning("لا توجد أنواع بمبيعات أكثر من 10 للطباعة");
      return;
    }
    if (
      !window.confirm(
        `طباعة الأنواع ذات المبيعات أكثر من 10؟\n\n${names.length} ملصق بالترتيب الأبجدي`
      )
    ) {
      return;
    }

    setIsPrintingAll(true);
    try {
      const count = await printTypeNameLabels(names);
      toast.success(`تم طباعة ${count} ملصق (مبيعات > 10 · أبجدي)`);
    } catch (err) {
      console.error("Print all types failed:", err);
      toast.error(err.message || "فشل الطباعة");
    } finally {
      setIsPrintingAll(false);
    }
  };

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4" dir="rtl">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={64} />
            <h1 className="text-2xl font-bold text-red-600">خطأ في التحميل</h1>
            <p className="mt-2 text-gray-600">معرف العلامة التجارية مفقود</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-3 pb-24 md:p-8 md:pb-8" dir="rtl">
      <ToastContainer position="top-center" rtl />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-5 md:mb-8 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Layers className="text-purple-600 shrink-0" size={26} />
              <h1 className="text-xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent truncate">
                أنواع {brandData ? brandData.name : "جاري التحميل..."}
              </h1>
            </div>
            <button
              onClick={() => navigate("/brands")}
              className="shrink-0 flex items-center gap-1 bg-white/80 backdrop-blur-sm text-gray-700 px-3 py-2 md:px-6 md:py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 text-sm md:text-base"
            >
              <ChevronRight size={18} />
              <span className="sm:hidden">رجوع</span>
              <span className="hidden sm:inline">العودة للعلامات التجارية</span>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
            <button
              onClick={handleDownloadBrandReport}
              disabled={isLoading || availableTypes.length === 0}
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 bg-indigo-600 text-white px-2 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md hover:bg-indigo-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 text-xs sm:text-sm font-bold"
              title="تحميل CSV للأنواع المتاحة ومبيعاتها"
            >
              <FileDown size={16} />
              <span className="sm:hidden">تقرير</span>
              <span className="hidden sm:inline">تقرير المتاح</span>
            </button>
            <button
              onClick={handlePrintAllTypes}
              disabled={isLoading || isPrintingAll || filteredTypes.length === 0}
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 bg-emerald-600 text-white px-2 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md hover:bg-emerald-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 text-xs sm:text-sm font-bold"
              title="طباعة الأنواع ذات المبيعات أكثر من 10"
            >
              <Printer size={16} className={isPrintingAll ? "animate-pulse" : ""} />
              {isPrintingAll ? "..." : (
                <>
                  <span className="sm:hidden">طباعة</span>
                  <span className="hidden sm:inline">طباعة (مبيعات &gt; 10)</span>
                </>
              )}
            </button>
            <button
              onClick={refreshData}
              disabled={isLoading}
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 bg-white/80 backdrop-blur-sm text-gray-700 px-2 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 disabled:opacity-50 text-xs sm:text-sm font-bold"
            >
              <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
              تحديث
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-3 md:px-6 md:py-4 rounded-2xl mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-3">
              <AlertCircle className="text-red-500 shrink-0" size={20} />
              <p className="text-sm md:text-base">{error}</p>
            </div>
            <button
              onClick={refreshData}
              className="text-red-600 hover:text-red-800 text-sm font-bold self-start sm:self-auto"
            >
              إعادة المحاولة
            </button>
          </div>
        )}

        {/* Stats and Actions */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 md:gap-6 mb-5 md:mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border border-white/30">
            <div className="flex items-center gap-2 mb-1 md:hidden">
              <Tag className="text-purple-600" size={16} />
              <h2 className="text-xs font-bold text-gray-600">الأنواع</h2>
            </div>
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Tag className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">عدد الأنواع</h2>
                  <p className="text-sm text-gray-600">جرابات الطباعة</p>
                </div>
              </div>
              <div className="text-4xl font-bold text-purple-600">{types.length}</div>
            </div>
            <div className="text-2xl font-black text-purple-600 md:hidden">{types.length}</div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border border-white/30">
            <div className="flex items-center gap-2 mb-1 md:hidden">
              <Package className="text-blue-600" size={16} />
              <h2 className="text-xs font-bold text-gray-600">قطع المخزن</h2>
            </div>
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Package className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">قطع المخزن</h2>
                  <p className="text-sm text-gray-600">ستوك الطباعة فقط</p>
                </div>
              </div>
              <div className="text-4xl font-bold text-blue-600">{unitsOnHand}</div>
            </div>
            <div className="text-2xl font-black text-blue-600 md:hidden">{unitsOnHand}</div>
          </div>
          <button
            type="button"
            onClick={() => setStockFilter(stockFilter === "restock" ? "all" : "restock")}
            className={`text-right bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border transition ${
              stockFilter === "restock" ? "border-amber-400 ring-2 ring-amber-200" : "border-white/30"
            }`}
          >
            <p className="text-xs md:text-sm text-gray-600">محتاج شراء</p>
            <p className="text-2xl md:text-4xl font-black md:font-bold text-amber-600 mt-0.5 md:mt-1">{restockCount}</p>
          </button>
          <button
            type="button"
            onClick={() => setStockFilter(stockFilter === "out" ? "all" : "out")}
            className={`text-right bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-6 border transition ${
              stockFilter === "out" ? "border-red-400 ring-2 ring-red-200" : "border-white/30"
            }`}
          >
            <p className="text-xs md:text-sm text-gray-600">خلص من المخزن</p>
            <p className="text-2xl md:text-4xl font-black md:font-bold text-red-600 mt-0.5 md:mt-1">{outCount}</p>
          </button>
        </div>

        {!isLoading && availableTypes.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl mb-6 border border-white/30 overflow-hidden">
            <button
              type="button"
              onClick={() => setIsHeatmapOpen((open) => !open)}
              className="w-full flex items-center justify-between gap-3 p-3 md:p-4 text-right hover:bg-white/50 transition"
              aria-expanded={isHeatmapOpen}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-indigo-100 rounded-xl shrink-0">
                  {isHeatmapOpen ? (
                    <ChevronUp className="text-indigo-700" size={20} />
                  ) : (
                    <ChevronDown className="text-indigo-700" size={20} />
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="text-base md:text-lg font-bold text-gray-800">خريطة مبيعات الأنواع المتاحة</h2>
                  <p className="text-xs md:text-sm text-gray-500 truncate">
                    {availableTypes.length} نوع متاح · إجمالي المبيعات {availableSalesTotal}
                    {availableTypes[0] ? ` · الأعلى ${availableTypes[0].name} (${availableTypes[0].sales || 0})` : ""}
                  </p>
                </div>
              </div>
              <span className="text-sm font-bold text-indigo-600 shrink-0">
                {isHeatmapOpen ? "إخفاء" : "عرض"}
              </span>
            </button>

            {isHeatmapOpen && (
              <div className="px-3 pb-4 md:px-6 md:pb-6">
                <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 mb-4">
                  <button
                    type="button"
                    onClick={handleDownloadBrandPdf}
                    disabled={isDownloadingPdf}
                    className="flex items-center justify-center gap-2 bg-white text-indigo-700 px-4 py-2.5 rounded-xl border border-indigo-200 hover:bg-indigo-50 font-bold text-sm disabled:opacity-50"
                  >
                    <FileDown size={16} />
                    {isDownloadingPdf ? "جاري تحميل PDF..." : "تحميل PDF"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadAllBrandsReport}
                    disabled={isExportingAll}
                    className="flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 font-bold text-sm disabled:opacity-50"
                  >
                    <FileSpreadsheet size={16} />
                    {isExportingAll ? "جاري التحميل..." : "CSV كل البراندات"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadAllBrandsPdf}
                    disabled={isDownloadingPdf}
                    className="flex items-center justify-center gap-2 bg-white text-gray-700 px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 font-bold text-sm disabled:opacity-50"
                  >
                    <FileDown size={16} />
                    {isDownloadingPdf ? "جاري تحميل PDF..." : "PDF كل البراندات"}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-gray-500">
                  <span>أقل مبيعات</span>
                  <div
                    className="h-2 flex-1 max-w-[220px] rounded-full"
                    style={{
                      background: "linear-gradient(to left, hsl(218,72%,90%), hsl(55,85%,62%), hsl(8,82%,52%))",
                    }}
                  />
                  <span>أكثر مبيعات</span>
                  <span className="mr-auto text-gray-400">
                    {minAvailableSales} → {maxAvailableSales}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {availableTypes.map((type) => {
                    const heat = getSalesHeatStyle(type.sales, minAvailableSales, maxAvailableSales);
                    return (
                      <div
                        key={type.id}
                        className="rounded-xl p-2.5 md:p-3 text-center shadow-sm min-h-[72px] md:min-h-[84px] flex flex-col justify-center"
                        style={heat}
                        title={`${type.name}: ${type.sales || 0} مبيعة`}
                      >
                        <div className="font-bold text-xs md:text-sm leading-tight">{type.name}</div>
                        <div className="text-lg md:text-xl font-black mt-1">{type.sales || 0}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-4 md:bg-white/80 md:backdrop-blur-sm md:rounded-2xl md:shadow-xl md:p-6 md:mb-6 md:border md:border-white/30 md:flex md:items-center md:justify-end">
          <button
            onClick={() => setNewType({ name: "", isAvailable: true })}
            disabled={isLoading}
            className="w-full md:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 md:px-8 md:py-4 rounded-xl shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 font-bold text-base md:text-lg disabled:opacity-50"
          >
            <Plus size={22} />
            إضافة نوع جديد
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md md:shadow-xl p-3 md:p-4 mb-4 md:mb-6 border border-white/30">
          <div className="relative mb-3">
            <Search className="absolute right-3 md:right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="بحث باسم النوع أو الرقم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 md:pr-12 pl-4 py-2.5 md:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
            />
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            {[
              { id: "all", label: "الكل" },
              { id: "restock", label: `محتاج شراء (${restockCount})` },
              { id: "out", label: `خلص (${outCount})` },
              { id: "belowStop", label: "تحت حد التوقف" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStockFilter(f.id)}
                className={`px-3 py-2.5 rounded-xl text-sm font-bold border transition ${
                  stockFilter === f.id
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Types list */}
        {isLoading ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6">
            <SkeletonLoader type="table" />
          </div>
        ) : filteredTypes.length || childSearchHits.length ? (
          <>
            {childSearchHits.length > 0 && (
              <div className="mb-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-950">
                <p className="font-bold mb-2">نتائج بحث في الأنواع التابعة</p>
                <ul className="space-y-1">
                  {childSearchHits.map((hit) => (
                    <li key={hit.id}>
                      <b>{hit.name}</b> — تابع لـ {hit.mainTypeName || hit.mainTypeId} · مالك المخزون: {hit.mainTypeName || hit.mainTypeId}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-3 md:hidden">
              {filteredTypes.map((type) => (
                <TypeMobileCard
                  key={type.id}
                  type={type}
                  isLoading={isLoading}
                  printingId={printingId}
                  isPrintingAll={isPrintingAll}
                  onToggleAvailability={handleToggleAvailability}
                  onAddStock={openAddStock}
                  onPrint={handlePrintType}
                  onEdit={openEditType}
                  onDelete={handleDeleteType}
                  onOpenSales={setSalesDetailType}
                />
              ))}
            </div>
            <div className="hidden md:block bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
              <table className="w-full min-w-[1100px] table-auto">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                    <th className="p-4 text-center font-bold text-purple-800">#ID</th>
                    <th className="p-4 text-right font-bold text-purple-800">اسم النوع</th>
                    <th className="p-4 text-center font-bold text-purple-800">يخدم</th>
                    <th className="p-4 text-center font-bold text-purple-800">المبيعات</th>
                    <th className="p-4 text-center font-bold text-purple-800">المخزن</th>
                    <th className="p-4 text-center font-bold text-purple-800">حد الإشعار</th>
                    <th className="p-4 text-center font-bold text-purple-800">حد التوقف</th>
                    <th className="p-4 text-center font-bold text-purple-800">الحالة</th>
                    <th className="p-4 text-center font-bold text-purple-800">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTypes.map((type) => (
                    <tr
                      key={type.id}
                      className={`border-b border-gray-200 hover:bg-white/60 transition-colors ${
                        (type.stockQuantity || 0) <= 0
                          ? "bg-red-50/50"
                          : type.isLowStock
                            ? "bg-amber-50/40"
                            : type.isAvailable
                              ? ""
                              : "bg-red-50/30"
                      }`}
                    >
                      <td className="p-4 text-center">
                        <span className="font-mono font-bold text-purple-600">#{type.id}</span>
                      </td>
                      <td className="text-xl p-2 font-medium">
                        <button
                          type="button"
                          onClick={() => setSalesDetailType(type)}
                          className="text-right font-medium hover:text-purple-700 underline decoration-purple-200 underline-offset-4"
                        >
                          {type.name}
                        </button>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                            نوع رئيسي
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                            يخدم {type.servesCount || 1} أنواع
                          </span>
                        </div>
                        {(type.servedTypes || []).length > 1 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {(type.servedTypes || []).map((s) => s.name).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center font-bold text-purple-700">{type.servesCount || 1}</td>
                      <td className="text-2xl p-2 text-center">
                        <button
                          type="button"
                          onClick={() => setSalesDetailType(type)}
                          className="w-full flex flex-col items-center justify-center gap-0.5"
                          title="عرض تفصيل المبيعات"
                        >
                          <div className="flex items-center justify-center gap-1">
                            <TrendingUp size={22} className="text-green-600" />
                            <span className="font-bold text-green-600">{type.totalSales ?? type.sales}</span>
                          </div>
                          {(type.servesCount || 1) > 1 && (
                            <span className="text-[10px] font-bold text-green-700/80">مجموع الشكل</span>
                          )}
                        </button>
                      </td>
                      <td className="p-2 text-center">
                        <div className={`text-2xl font-bold ${
                          (type.stockQuantity || 0) <= 0
                            ? "text-red-600"
                            : type.isLowStock
                              ? "text-amber-600"
                              : "text-blue-700"
                        }`}>
                          {type.stockQuantity ?? 0}
                        </div>
                        {type.isLowStock && (
                          <span className="inline-block mt-1 text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            اشترِ تاني
                          </span>
                        )}
                        {(type.stockQuantity || 0) <= 0 && (
                          <span className="inline-block mt-1 text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            خلص
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-center font-bold text-gray-700">{type.lowStockThreshold ?? 0}</td>
                      <td className="p-2 text-center font-bold text-gray-500">{type.unavailableThreshold ?? 0}</td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => handleToggleAvailability(type)}
                          disabled={isLoading}
                          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 flex items-center justify-center gap-2 mx-auto ${
                            type.isAvailable
                              ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200"
                              : "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                          }`}
                        >
                          {type.isAvailable ? (
                            <>
                              <CheckCircle size={16} />
                              متاح
                            </>
                          ) : (
                            <>
                              <XCircle size={16} />
                              غير متاح
                            </>
                          )}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            onClick={() => openAddStock(type)}
                            disabled={isLoading}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                            title="إضافة قطع للمخزن"
                          >
                            <PackagePlus size={14} />
                            إضافة ستوك
                          </button>
                          <button
                            onClick={() => handlePrintType(type)}
                            disabled={isLoading || printingId === type.id || isPrintingAll}
                            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                            title="طباعة اسم الموبايل على الملصق"
                          >
                            <Printer size={14} className={printingId === type.id ? "animate-pulse" : ""} />
                            {printingId === type.id ? "..." : "طباعة"}
                          </button>
                          <button
                            onClick={() => openEditType(type)}
                            disabled={isLoading}
                            className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2"
                          >
                            <Edit size={14} />
                            تعديل
                          </button>
                          <button
                            onClick={() => handleDeleteType(type.id)}
                            disabled={isLoading}
                            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2"
                          >
                            <Trash2 size={14} />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-10 md:p-16 text-center">
            <Layers className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-xl md:text-2xl text-gray-700 mb-2">لا توجد أنواع</p>
            <p className="text-base md:text-lg text-gray-500">قم بإضافة نوع جديد للبدء</p>
          </div>
        )}

        {/* Create Modal */}
        {newType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 md:p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-xl md:text-2xl font-bold">إضافة نوع جديد</h2>
                <button
                  onClick={() => setNewType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateType} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم النوع
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newType.name}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
                      placeholder="أدخل اسم النوع"
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-xl">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      id="isAvailable"
                      checked={newType.isAvailable}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="isAvailable" className="font-medium text-gray-700">
                      متاح للعرض
                    </label>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setNewType(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    إنشاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-4 md:p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-xl md:text-2xl font-bold">تعديل النوع #{editingType.id}</h2>
                <button
                  onClick={() => setEditingType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdateType} className="p-4 md:p-6 max-h-[70vh] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم النوع
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingType.name}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      placeholder="أدخل اسم النوع"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      رصيد المخزن (الرقم الكامل)
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="stockQuantity"
                      value={editingType.stockQuantity ?? 0}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">للتعديل المباشر لو فيه بضاعة تالفة أو جرد.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-right font-medium text-gray-700 mb-2">
                        حد الإشعار
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="lowStockThreshold"
                        value={editingType.lowStockThreshold ?? 0}
                        onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-right font-medium text-gray-700 mb-2">
                        حد التوقف
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="unavailableThreshold"
                        value={editingType.unavailableThreshold ?? 0}
                        onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">لما المخزون يوصل لحد التوقف أو صفر، الموديل يختفي من الموقع تلقائيًا. إضافة مخزون فوق الحد ترجّعه للبيع.</p>

                  <div className="p-3 bg-purple-50 rounded-xl">
                    <p className="font-medium text-gray-700 mb-1">الأنواع التي يخدمها هذا الكفر</p>
                    <p className="text-xs text-gray-500 mb-2">
                      النوع الرئيسي نفسه يظهر للعميل، بالإضافة لأي موديلات تابعة تستخدم نفس المخزون.
                    </p>
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setChildPickerScope("brand")}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold border ${
                          childPickerScope === "brand"
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-700 border-purple-200"
                        }`}
                      >
                        نفس البراند
                      </button>
                      <button
                        type="button"
                        onClick={() => setChildPickerScope("all")}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold border ${
                          childPickerScope === "all"
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-700 border-purple-200"
                        }`}
                      >
                        كل البراندات
                      </button>
                    </div>
                    <input
                      type="text"
                      value={childPickerSearch}
                      onChange={(e) => setChildPickerSearch(e.target.value)}
                      placeholder="بحث باسم الموديل..."
                      className="w-full mb-2 p-2.5 border-2 border-purple-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      <label className="flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-100">
                        <input type="checkbox" checked readOnly className="w-5 h-5 text-purple-600 rounded" />
                        <span className="text-sm text-gray-800">{editingType.name} (رئيسي)</span>
                      </label>
                      {(() => {
                        const source = catalogTypes.length ? catalogTypes : types;
                        const selectedIds = editingType.childTypeIds || [];
                        const brandNameOf = (t) =>
                          brands.find((b) => String(b.brandId) === String(t.brandId))?.name || "";
                        const eligible = source.filter((t) => {
                          if (t.id === editingType.id) return false;
                          return !source.some((c) => c.mainTypeId === t.id);
                        });
                        const visible = eligible.filter((t) => {
                          if (selectedIds.includes(t.id)) return true;
                          if (childPickerScope === "brand" && String(t.brandId) !== String(brandId)) {
                            return false;
                          }
                          const term = childPickerSearch.trim().toLowerCase();
                          if (!term) return true;
                          return (
                            t.name.toLowerCase().includes(term) ||
                            String(t.id).includes(term) ||
                            brandNameOf(t).toLowerCase().includes(term)
                          );
                        });
                        if (visible.length === 0) {
                          return (
                            <p className="text-xs text-gray-500 p-2">لا توجد نتائج مطابقة للبحث.</p>
                          );
                        }
                        return visible.map((t) => {
                          const checked = selectedIds.includes(t.id);
                          const otherOwner = t.mainTypeId && t.mainTypeId !== editingType.id;
                          const brandName = brandNameOf(t);
                          return (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-100"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const current = editingType.childTypeIds || [];
                                  setEditingType({
                                    ...editingType,
                                    childTypeIds: checked
                                      ? current.filter((id) => id !== t.id)
                                      : [...current, t.id],
                                  });
                                }}
                                className="w-5 h-5 text-purple-600 rounded"
                              />
                              <span className="text-sm text-gray-800">
                                {t.name}
                                {brandName ? ` · ${brandName}` : ""}
                                {otherOwner ? ` (تابع حالياً لـ ${t.mainTypeName || t.mainTypeId})` : ""}
                              </span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      id="editIsAvailable"
                      checked={editingType.isAvailable}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="editIsAvailable" className="font-medium text-gray-700">
                      متاح للعرض
                    </label>
                  </div>

                  <div className="p-3 bg-indigo-50 rounded-xl">
                    <p className="font-medium text-gray-700 mb-2">أنواع الكفر المتاحة</p>
                    {caseStyles.length === 0 ? (
                      <p className="text-sm text-gray-500">لا توجد أنواع كفر. أضفها من صفحة الكتالوج أولاً.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {caseStyles.map((style) => {
                          const checked = (editingType.caseStyleIds || []).includes(style.caseStyleId);
                          return (
                            <label
                              key={style.caseStyleId}
                              className="flex items-center gap-2 p-2 bg-white rounded-lg border border-indigo-100"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const current = editingType.caseStyleIds || [];
                                  setEditingType({
                                    ...editingType,
                                    caseStyleIds: checked
                                      ? current.filter((id) => id !== style.caseStyleId)
                                      : [...current, style.caseStyleId],
                                  });
                                }}
                                className="w-5 h-5 text-indigo-600 rounded"
                              />
                              <span className="text-sm text-gray-800">
                                {displayAr(style.name)}
                                {style.isActive === false ? " (معطّل)" : ""}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditingType(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {salesDetailType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 md:p-5 flex justify-between items-center">
                <h2 className="text-lg md:text-xl font-bold leading-snug">مبيعات {salesDetailType.name}</h2>
                <button
                  type="button"
                  onClick={() => setSalesDetailType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="p-4 md:p-5 overflow-y-auto space-y-3">
                <div className="rounded-xl bg-green-50 border border-green-100 px-4 py-3 text-center">
                  <p className="text-xs font-medium text-green-700">مجموع شكل الجراب</p>
                  <p className="text-3xl font-black text-green-700 tabular-nums">
                    {salesDetailType.totalSales ?? salesDetailType.sales ?? 0}
                  </p>
                </div>
                <p className="text-sm font-bold text-gray-700">تفصيل كل موديل</p>
                <div className="space-y-2">
                  {(salesDetailType.servedTypes || []).map((row) => (
                    <div
                      key={row.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50"
                    >
                      <div>
                        <p className="font-bold text-gray-900">{row.name}</p>
                        <p className="text-xs text-gray-500">
                          {row.isParent ? "النوع الرئيسي" : "نوع تابع"}
                        </p>
                      </div>
                      <p className="text-xl font-black text-green-700 tabular-nums">{row.sales || 0}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {addingStockFor && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-lg md:text-xl font-bold leading-snug">إضافة ستوك · {addingStockFor.name}</h2>
                <button
                  onClick={() => {
                    setAddingStockFor(null);
                    setAddStockQty("");
                  }}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleAddStock} className="p-6 space-y-4">
                <p className="text-gray-600">
                  الرصيد الحالي: <span className="font-bold text-blue-700">{addingStockFor.stockQuantity ?? 0}</span>
                </p>
                <div>
                  <label className="block text-right font-medium text-gray-700 mb-2">
                    عدد القطع اللي ضفتها للمخزن
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={addStockQty}
                    onChange={(e) => setAddStockQty(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                    placeholder="مثال: 30"
                    required
                    autoFocus
                  />
                </div>
                {Number(addStockQty) > 0 && (
                  <p className="text-sm text-gray-500">
                    الإجمالي الجديد: {(addingStockFor.stockQuantity || 0) + Number(addStockQty)}
                  </p>
                )}
                <div className="flex justify-center gap-4 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAddingStockFor(null);
                      setAddStockQty("");
                    }}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold transition"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 font-bold transition"
                  >
                    <PackagePlus size={20} />
                    إضافة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {/* Floating Button to Scroll to Top - أضف هذا الزر الجديد */}
      <div className="fixed bottom-5 left-5 md:bottom-6 md:right-6 md:left-auto z-40">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
          title="العودة إلى الأعلى"
        >
          <ChevronUp size={24} />
        </button>
      </div>
    </div>
  );
};

export default TypesPage;